import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdFitnessCenter, MdInfo, MdLock, MdOpenInNew } from 'react-icons/md';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getFirestore,
  onSnapshot,
  query,
  runTransaction,
  updateDoc,
  where,
} from 'firebase/firestore';
import Card from 'components/card/Card';
import { AuthContext } from 'contexts/AuthContext';
import { app, functions } from 'lib/firebase';
import ClassDetailsModal from 'views/admin/calendar/components/InstructorClassDetailsModal';
import ClaimSuccessAnimation from './components/ClaimSuccessAnimation';
import OpportunityFinderMap from './components/OpportunityFinderMap';
import {
  buildClaimClassUpdate,
  formatClassDateTimeLA,
  getClassDisplayText,
  getClassDurationMinutes,
} from 'utils/instructorClassWorkflows';
import {
  buildClassActivityActor,
  buildInstructorClassClaimActivity,
} from 'utils/classActivityLogger';
import { TRAVEL_DISTANCE_OPTIONS } from 'utils/instructorProfile';
import { ACCOUNT_STATUS_STRIKE_PAUSED } from 'utils/lateCancellationStrikes';
import {
  areOpportunityFacilitiesLoaded,
  buildOpportunityMapItems,
  chunkClassIdsForFirestore,
  filterClaimableOpportunityClasses,
  getOpportunityHydrationState,
  getLatLngFromLocation,
  getNearbyOpportunityProfileIssue,
  normalizeNearbyClassIds,
  validateOpportunityClaim,
} from 'utils/instructorOpportunities';
import { getClassTypeIcon } from 'utils/classIcons';

const db = getFirestore(app);

function OpportunityCard({ classData, facility, onClaim, onOpenDetails, isClaiming, stripeSetupRequired }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('gray.700', 'gray.400');
  const duration = getClassDurationMinutes(classData);
  const className = getClassDisplayText(
    classData.ClassName || classData.ClassType,
    'Class Opportunity'
  );
  const classIcon = getClassTypeIcon(classData.ClassName || classData.ClassType);
  const facilityName = getClassDisplayText(
    facility?.FacilityName || facility?.FirstName || classData.FacilityName,
    'Facility'
  );
  const facilityAddress = getClassDisplayText(
    facility?.Address || classData.Address,
    'Address unavailable'
  );
  const classDescription = getClassDisplayText(classData.ClasssDescription, '');

  return (
    <Card
      p="22px"
      cursor="pointer"
      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
      transition="all 0.2s ease"
      onClick={onOpenDetails}
    >
      <Flex direction="column" h="100%" gap="14px">
        <Flex align="flex-start" justify="space-between" gap="12px">
          <Flex align="center" gap="12px" minW="0">
            <Flex
              data-testid="opportunity-class-icon"
              w="44px"
              h="44px"
              borderRadius="12px"
              bg="brand.500"
              align="center"
              justify="center"
              flexShrink="0"
            >
              {classIcon ? (
                <Image
                  src={classIcon}
                  alt={className}
                  w="28px"
                  h="28px"
                  objectFit="contain"
                  filter="brightness(0) invert(1)"
                />
              ) : (
                <Icon as={MdFitnessCenter} color="white" w="22px" h="22px" />
              )}
            </Flex>
            <Box minW="0">
              <Text color={textColor} fontSize="lg" fontWeight="800" noOfLines={1}>
                {className}
              </Text>
              <Text color={textColorSecondary} fontSize="sm" mt="6px">
                {formatClassDateTimeLA(classData.Date)}
              </Text>
            </Box>
          </Flex>
          <Badge colorScheme="blue" borderRadius="8px" px="10px">
            {duration} min
          </Badge>
        </Flex>
        <Box>
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {facilityName}
          </Text>
          <Text color={textColorSecondary} fontSize="sm" mt="4px">
            {facilityAddress}
          </Text>
        </Box>
        {classDescription && (
          <Text color={textColorSecondary} fontSize="sm" noOfLines={3}>
            {classDescription}
          </Text>
        )}
        <Button
          mt="auto"
          colorScheme="brand"
          onClick={(event) => {
            event.stopPropagation();
            onClaim();
          }}
          isLoading={isClaiming}
          isDisabled={stripeSetupRequired}
        >
          Claim Class
        </Button>
      </Flex>
    </Card>
  );
}

function TransparentStripeSetupLock({ onOpenStripeSetup, isLoading }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('gray.700', 'gray.300');
  const overlayBg = useColorModeValue('rgba(255, 255, 255, 0.76)', 'rgba(11, 20, 55, 0.72)');

  return (
    <Flex
      position="absolute"
      inset="0"
      zIndex="overlay"
      align="center"
      justify="center"
      p={{ base: '18px', md: '28px' }}
      bg={overlayBg}
      backdropFilter="blur(3px)"
      borderRadius="18px"
    >
      <Card maxW="520px" p="28px" border="1px solid" borderColor="orange.200" boxShadow="2xl">
        <Flex direction="column" align="center" textAlign="center" gap="14px">
          <Flex w="52px" h="52px" borderRadius="16px" bg="orange.50" align="center" justify="center">
            <Icon as={MdLock} color="orange.500" w="28px" h="28px" />
          </Flex>
          <Box>
            <Text color={textColor} fontSize="xl" fontWeight="800">
              Complete Stripe setup to claim classes
            </Text>
            <Text color={textColorSecondary} fontSize="sm" mt="8px">
              You can still view your portal, but claiming new opportunities requires active payouts and charges.
            </Text>
          </Box>
          <Button
            colorScheme="orange"
            rightIcon={<Icon as={MdOpenInNew} />}
            isLoading={isLoading}
            onClick={onOpenStripeSetup}
          >
            Open Stripe Setup
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
}

function TravelDistancePrompt({
  currentDistanceValue,
  isUpdating,
  onSelectDistance,
}) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('gray.700', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const bannerBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const bannerBorder = useColorModeValue('blue.200', 'brand.500');
  const selectedBorder = useColorModeValue('brand.500', 'brand.300');
  const selectedBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const [showTravelDistanceOptions, setShowTravelDistanceOptions] = useState(false);

  return (
    <Card p={{ base: '16px', md: '18px' }} mb="20px" bg={bannerBg} border="1px solid" borderColor={bannerBorder}>
      <Flex
        align={{ base: 'flex-start', md: 'center' }}
        justify="space-between"
        direction={{ base: 'column', md: 'row' }}
        gap="12px"
      >
        <Flex align="center" gap="12px">
          <Flex
            w="42px"
            h="42px"
            borderRadius="14px"
            bg="brand.500"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon as={MdInfo} color="white" w="22px" h="22px" />
          </Flex>
          <Box>
            <Text color={textColor} fontSize="md" fontWeight="800">
              Not seeing opportunities?
            </Text>
            <Text color={textColorSecondary} fontSize="sm" mt="4px">
              Try changing your driving distance you are willing to travel.
            </Text>
          </Box>
        </Flex>
        <Button
          colorScheme="brand"
          variant={showTravelDistanceOptions ? 'outline' : 'solid'}
          flexShrink={0}
          onClick={() => setShowTravelDistanceOptions((current) => !current)}
        >
          {showTravelDistanceOptions ? 'Hide Options' : 'Change Driving Distance'}
        </Button>
      </Flex>

      {showTravelDistanceOptions && (
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="12px" mt="16px">
          {TRAVEL_DISTANCE_OPTIONS.map((option) => {
            const isSelected = currentDistanceValue === option.value;

            return (
              <Button
                key={option.value}
                h="auto"
                minH="92px"
                p="14px"
                whiteSpace="normal"
                alignItems="flex-start"
                justifyContent="flex-start"
                textAlign="left"
                variant="outline"
                borderWidth="2px"
                borderColor={isSelected ? selectedBorder : borderColor}
                bg={isSelected ? selectedBg : 'transparent'}
                isLoading={isUpdating && isSelected}
                onClick={() => onSelectDistance(option)}
              >
                <Box>
                  <Text color={textColor} fontSize="md" fontWeight="800">
                    {option.title}
                  </Text>
                  <Text color={textColorSecondary} fontSize="sm" mt="5px">
                    {option.description}
                  </Text>
                </Box>
              </Button>
            );
          })}
        </SimpleGrid>
      )}
    </Card>
  );
}

export default function InstructorOpportunities() {
  const {
    user,
    userDocId,
    userData,
    refreshUserData,
    stripeSetupComplete,
    stripeSetupRequired,
    stripeSetupLinkLoading,
    openStripeSetup,
  } = useContext(AuthContext);
  const { classId: deepLinkedClassId } = useParams();
  const [opportunities, setOpportunities] = useState([]);
  const [opportunityClassIds, setOpportunityClassIds] = useState([]);
  const [facilitiesById, setFacilitiesById] = useState({});
  const [selectedOpportunityClass, setSelectedOpportunityClass] = useState(null);
  const [nearbyClassIdsLoaded, setNearbyClassIdsLoaded] = useState(false);
  const [isLoadingClassDocs, setIsLoadingClassDocs] = useState(false);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [error, setError] = useState('');
  const [claimingClassId, setClaimingClassId] = useState('');
  const [pendingClaimClass, setPendingClaimClass] = useState(null);
  const [claimedClassPreview, setClaimedClassPreview] = useState(null);
  const [showClaimSuccess, setShowClaimSuccess] = useState(false);
  const [updatingTravelDistance, setUpdatingTravelDistance] = useState('');
  const toast = useToast();
  const classDocsById = useRef({});
  const cancelClaimRef = useRef(null);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('gray.700', 'gray.400');
  const brandColor = useColorModeValue('brand.500', 'brand.400');

  const stripeComplete = Boolean(stripeSetupComplete);
  const instructorLocation = getLatLngFromLocation(userData?.location);
  const opportunityMapItems = buildOpportunityMapItems(opportunities, facilitiesById);
  const opportunityHydration = getOpportunityHydrationState({
    nearbyClassIdsLoaded,
    isLoadingClassDocs,
    isLoadingFacilities: isLoadingFacilities || !areOpportunityFacilitiesLoaded(opportunities, facilitiesById),
    opportunities,
  });
  const instructorName = getClassDisplayText(
    [userData?.FirstName, userData?.LastName].filter(Boolean).join(' '),
    'Instructor Location'
  );
  const shouldShowTravelDistancePrompt = !opportunityHydration.isHydrating && opportunities.length < 3;

  useEffect(() => {
    let isActive = true;

    const loadOpportunities = async () => {
      if (!userDocId) {
        setOpportunityClassIds([]);
        setOpportunities([]);
        setNearbyClassIdsLoaded(true);
        setIsLoadingClassDocs(false);
        setIsLoadingFacilities(false);
        return;
      }

      setNearbyClassIdsLoaded(false);
      setIsLoadingClassDocs(false);
      setIsLoadingFacilities(false);
      try {
        let classIds = [];
        if (deepLinkedClassId) {
          classIds = normalizeNearbyClassIds([deepLinkedClassId]);
        } else {
          const nearbyProfileIssue = getNearbyOpportunityProfileIssue(userData);
          if (nearbyProfileIssue) {
            if (isActive) {
              setError(nearbyProfileIssue);
              setOpportunityClassIds([]);
              setOpportunities([]);
              setNearbyClassIdsLoaded(true);
            }
            return;
          }

          if (isActive) setError('');
          const getNewNearbyClasses = httpsCallable(functions, 'getNewNearbyClasses');
          const result = await getNewNearbyClasses({ uid: userDocId });
          classIds = normalizeNearbyClassIds(result?.data?.classes);
        }

        if (isActive) {
          setOpportunityClassIds(classIds);
          setNearbyClassIdsLoaded(true);
        }
      } catch (nearbyError) {
        console.error('Error loading nearby classes:', nearbyError);
        if (isActive) {
          setError('Unable to load nearby class opportunities.');
          setNearbyClassIdsLoaded(true);
          setIsLoadingClassDocs(false);
          setIsLoadingFacilities(false);
        }
      }
    };

    loadOpportunities();

    return () => {
      isActive = false;
    };
  }, [deepLinkedClassId, userDocId, userData]);

  useEffect(() => {
    classDocsById.current = {};

    if (!nearbyClassIdsLoaded) {
      setOpportunities([]);
      setIsLoadingClassDocs(false);
      return undefined;
    }

    if (opportunityClassIds.length === 0) {
      setOpportunities([]);
      setIsLoadingClassDocs(false);
      return undefined;
    }

    setIsLoadingClassDocs(true);

    const publishClaimableClasses = () => {
      const claimableClasses = filterClaimableOpportunityClasses(Object.values(classDocsById.current))
        .sort((a, b) => Number(a.Date || 0) - Number(b.Date || 0));

      setOpportunities(claimableClasses);
      setIsLoadingClassDocs(false);
    };

    const unsubscribes = chunkClassIdsForFirestore(opportunityClassIds).map((idChunk) => {
      const unsubscribe = onSnapshot(query(
        collection(db, 'Classes'),
        where(documentId(), 'in', idChunk)
      ), (classSnapshot) => {
        idChunk.forEach((classId) => {
          delete classDocsById.current[classId];
        });

        classSnapshot.forEach((classDoc) => {
          classDocsById.current[classDoc.id] = { id: classDoc.id, ...classDoc.data() };
        });

        publishClaimableClasses();
      }, (snapshotError) => {
        console.error('Error listening to nearby classes:', snapshotError);
        setError('Unable to keep nearby class opportunities updated.');
        setIsLoadingClassDocs(false);
      });

      return unsubscribe;
    });

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [nearbyClassIdsLoaded, opportunityClassIds]);

  useEffect(() => {
    if (!selectedOpportunityClass) return;

    const updatedClass = opportunities.find((classData) => classData.id === selectedOpportunityClass.id);
    if (updatedClass) {
      setSelectedOpportunityClass(updatedClass);
    } else if (selectedOpportunityClass.AssignedInstructorID !== userDocId) {
      setSelectedOpportunityClass(null);
    }
  }, [opportunities, selectedOpportunityClass, userDocId]);

  useEffect(() => {
    if (!showClaimSuccess) return undefined;

    const timeoutId = setTimeout(() => setShowClaimSuccess(false), 1800);
    return () => clearTimeout(timeoutId);
  }, [showClaimSuccess]);

  useEffect(() => {
    let isActive = true;
    const missingFacilityIds = Array.from(new Set(
      opportunities
        .map((classData) => classData.FacilityID)
        .filter((facilityId) => facilityId && !facilitiesById[facilityId])
    ));

    if (opportunities.length === 0 || missingFacilityIds.length === 0) {
      setIsLoadingFacilities(false);
      return undefined;
    }

    const loadFacilities = async () => {
      setIsLoadingFacilities(true);
      const entries = await Promise.all(missingFacilityIds.map(async (facilityId) => {
        const facilityDoc = await getDoc(doc(db, 'Facilities', facilityId));
        return [facilityId, facilityDoc.exists() ? facilityDoc.data() : null];
      }));

      if (!isActive) return;
      setFacilitiesById((current) => ({
        ...current,
        ...Object.fromEntries(entries),
      }));
      setIsLoadingFacilities(false);
    };

    loadFacilities();

    return () => {
      isActive = false;
    };
  }, [opportunities, facilitiesById]);

  const handleOpenStripeSetup = async () => {
    try {
      await openStripeSetup();
    } catch (setupError) {
      toast({
        title: 'Unable to open Stripe setup',
        description: setupError.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleUpdateTravelDistance = async (option) => {
    if (!userDocId || !option?.value || option.value === userData?.distanceValue) return;

    setUpdatingTravelDistance(option.value);
    try {
      await updateDoc(doc(db, 'Users', userDocId), {
        distanceValue: option.value,
      });
      await refreshUserData?.();
      toast({
        title: 'Driving distance updated',
        description: 'Refreshing nearby opportunities.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (distanceError) {
      toast({
        title: 'Unable to update driving distance',
        description: distanceError.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUpdatingTravelDistance('');
    }
  };

  const openClaimConfirmation = (classData) => {
    setPendingClaimClass(classData);
  };

  const closeClaimConfirmation = () => {
    if (!claimingClassId) setPendingClaimClass(null);
  };

  const handleClaimClass = async () => {
    const classId = pendingClaimClass?.id;
    if (!stripeComplete || !userDocId) return;
    if (!classId) return;

    setClaimingClassId(classId);
    try {
      const actor = buildClassActivityActor({ user, userDocId, userData });
      let claimedClassData = null;

      await runTransaction(db, async (transaction) => {
        const classRef = doc(db, 'Classes', classId);
        const activityRef = doc(collection(db, 'Classes', classId, 'Activity'));
        const classSnapshot = await transaction.get(classRef);

        if (!classSnapshot.exists()) throw new Error('Class no longer exists.');
        const classSnapshotData = classSnapshot.data();
        claimedClassData = {
          id: classId,
          ...pendingClaimClass,
          ...classSnapshotData,
          AssignedInstructorID: userDocId,
        };

        validateOpportunityClaim({
          classData: classSnapshotData,
          stripeComplete,
          instructorAccountStatus: userData?.AccountStatus,
        });

        transaction.update(classRef, buildClaimClassUpdate(userDocId));
        transaction.set(activityRef, buildInstructorClassClaimActivity({
          actor,
          instructorId: userDocId,
          classId,
          classData: classSnapshotData,
        }));
      });

      setOpportunities((current) => current.filter((classData) => classData.id !== classId));
      setClaimedClassPreview(claimedClassData);
      setSelectedOpportunityClass(claimedClassData);
      setShowClaimSuccess(true);
      toast({
        title: 'Class claimed',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      setPendingClaimClass(null);
    } catch (claimError) {
      toast({
        title: 'Unable to claim class',
        description: claimError.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setClaimingClassId('');
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="instructor-opportunities-page">
          {error && (
            <Alert status="warning" borderRadius="12px" mb="20px">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
          )}
          {shouldShowTravelDistancePrompt && (
            <TravelDistancePrompt
              currentDistanceValue={userData?.distanceValue}
              isUpdating={Boolean(updatingTravelDistance)}
              onSelectDistance={handleUpdateTravelDistance}
            />
          )}

          <Box id="opportunities-map-widget" position="relative" borderRadius="18px">
            <Flex direction="column" gap="20px">
          {(opportunityHydration.isHydrating || opportunities.length > 0) && (
            <Box
              h={{ base: '360px', lg: '440px' }}
              borderRadius="16px"
              overflow="hidden"
              boxShadow="0 8px 24px rgba(43, 132, 196, 0.16)"
            >
              <OpportunityFinderMap
                instructorLocation={instructorLocation}
                instructorName={instructorName}
                opportunities={opportunityMapItems}
                isLoading={opportunityHydration.isHydrating}
                selectedClassId={selectedOpportunityClass?.id || ''}
                onOpportunityClick={(classData) => setSelectedOpportunityClass(classData)}
                locationBadgeLabel="Your Location"
                defaultZoom={9}
                focusZoom={9}
                showRadiusControls={false}
                showSearchRadius={false}
                initialSearchRadius={25}
              />
            </Box>
          )}

          <Box id="opportunities-list-widget">
            {opportunityHydration.isHydrating ? (
              <Card p="28px">
                <Flex align="center" gap="12px">
                  <Spinner color={brandColor} />
                  <Text color={textColorSecondary}>Loading nearby classes...</Text>
                </Flex>
              </Card>
            ) : opportunityHydration.showEmptyState ? (
              <Card p="28px">
                <Text color={textColor} fontSize="lg" fontWeight="700">
                  No nearby opportunities right now
                </Text>
                <Text color={textColorSecondary} fontSize="sm" mt="6px">
                  Check back later for open classes in your travel area.
                </Text>
              </Card>
            ) : opportunities.length > 0 ? (
              <>
                {/* class cards below the map */}
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="20px">
                  {opportunities.map((classData) => (
                    <OpportunityCard
                      key={classData.id}
                      classData={classData}
                      facility={facilitiesById[classData.FacilityID]}
                      onOpenDetails={() => setSelectedOpportunityClass(classData)}
                      onClaim={() => openClaimConfirmation(classData)}
                      isClaiming={claimingClassId === classData.id}
                      stripeSetupRequired={stripeSetupRequired}
                    />
                  ))}
                </SimpleGrid>
              </>
            ) : null}
          </Box>
            </Flex>
        {stripeSetupRequired && (
          <TransparentStripeSetupLock
            onOpenStripeSetup={handleOpenStripeSetup}
            isLoading={stripeSetupLinkLoading}
          />
        )}
      </Box>

      <ClassDetailsModal
        isOpen={Boolean(selectedOpportunityClass)}
        onClose={() => setSelectedOpportunityClass(null)}
        classData={selectedOpportunityClass}
        facility={selectedOpportunityClass ? facilitiesById[selectedOpportunityClass.FacilityID] : null}
        onClaimClass={() => openClaimConfirmation(selectedOpportunityClass)}
        isClaiming={claimingClassId === selectedOpportunityClass?.id}
        canClaim={stripeComplete && userData?.AccountStatus !== ACCOUNT_STATUS_STRIKE_PAUSED}
      />

      <ClaimSuccessAnimation
        isVisible={showClaimSuccess}
        classData={claimedClassPreview}
      />

      <AlertDialog
        isOpen={Boolean(pendingClaimClass)}
        leastDestructiveRef={cancelClaimRef}
        onClose={closeClaimConfirmation}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="800">
              Claim this class?
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text color={textColorSecondary} fontSize="sm">
                This will assign the class to you and remove it from nearby opportunities for other instructors.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelClaimRef} onClick={closeClaimConfirmation} isDisabled={Boolean(claimingClassId)}>
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                onClick={handleClaimClass}
                isLoading={claimingClassId === pendingClaimClass?.id}
                ml={3}
              >
                Claim Class
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
